import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calculator, MapPin, Truck, DollarSign, AlertTriangle, 
  Phone, ArrowRight, Info, CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" }, { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" }, { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" }, { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" }, { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" }, { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" }, { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" }, { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" },
];

const EQUIPMENT_TYPES = [
  { value: "Dry Van", label: "Dry Van", description: "Enclosed trailer for general freight" },
  { value: "Flatbed", label: "Flatbed", description: "Open trailer for oversized/heavy loads" },
  { value: "Reefer", label: "Refrigerated (Reefer)", description: "Temperature-controlled trailer" },
  { value: "Step Deck", label: "Step Deck", description: "Lower deck for tall freight" },
  { value: "Container", label: "Container/Intermodal", description: "Standard shipping containers" },
  { value: "Power Only", label: "Power Only", description: "Tractor only, customer provides trailer" },
];

const QuoteCalculatorPage = () => {
  const [formData, setFormData] = useState({
    origin_state: '',
    origin_city: '',
    destination_state: '',
    destination_city: '',
    equipment_type: '',
  });
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async (e) => {
    e.preventDefault();
    
    if (!formData.origin_state || !formData.destination_state || !formData.equipment_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/quote/estimate`, formData);
      setEstimate(response.data);
    } catch (error) {
      toast.error('Failed to calculate estimate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Hero Section */}
      <section className="bg-zinc-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-red-600 flex items-center justify-center mx-auto mb-6">
            <Calculator className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Instant Freight Quote Calculator
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Get a quick estimate for your shipment based on historical rates. 
            Add city for more accurate pricing. For exact quotes, contact our team.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 lg:p-12 border border-zinc-200 shadow-sm">
            <form onSubmit={handleCalculate} className="space-y-8">
              {/* Origin Section */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-600" />
                  Origin Location
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                      State *
                    </label>
                    <Select
                      value={formData.origin_state}
                      onValueChange={(value) => setFormData({ ...formData, origin_state: value })}
                    >
                      <SelectTrigger className="h-12 border-zinc-300" data-testid="calc-origin">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state.value} value={state.value}>
                            {state.label} ({state.value})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                      City <span className="text-zinc-400 font-normal">(for more accurate estimate)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.origin_city}
                      onChange={(e) => setFormData({ ...formData, origin_city: e.target.value })}
                      className="w-full h-12 px-4 border border-zinc-300 focus:border-zinc-900 outline-none"
                      placeholder="e.g., Los Angeles, Houston"
                      data-testid="calc-origin-city"
                    />
                  </div>
                </div>
              </div>

              {/* Destination Section */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-red-600" />
                  Destination Location
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                      State *
                    </label>
                    <Select
                      value={formData.destination_state}
                      onValueChange={(value) => setFormData({ ...formData, destination_state: value })}
                    >
                      <SelectTrigger className="h-12 border-zinc-300" data-testid="calc-destination">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state.value} value={state.value}>
                            {state.label} ({state.value})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                      City <span className="text-zinc-400 font-normal">(for more accurate estimate)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.destination_city}
                      onChange={(e) => setFormData({ ...formData, destination_city: e.target.value })}
                      className="w-full h-12 px-4 border border-zinc-300 focus:border-zinc-900 outline-none"
                      placeholder="e.g., Dallas, Chicago"
                      data-testid="calc-destination-city"
                    />
                  </div>
                </div>
              </div>

              {/* Equipment Type */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  <Truck className="w-4 h-4 inline mr-1" />
                  Equipment Type *
                </label>
                <Select
                  value={formData.equipment_type}
                  onValueChange={(value) => setFormData({ ...formData, equipment_type: value })}
                >
                  <SelectTrigger className="h-12 border-zinc-300" data-testid="calc-equipment">
                    <SelectValue placeholder="Select equipment type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <span className="font-medium">{type.label}</span>
                          <span className="text-zinc-500 text-sm ml-2">- {type.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-4 font-bold uppercase tracking-wide transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="calc-submit"
              >
                {loading ? 'Calculating...' : (
                  <>
                    <Calculator className="w-5 h-5" />
                    Calculate Estimate
                  </>
                )}
              </button>
            </form>

            {/* Estimate Results */}
            {estimate && (
              <div className="mt-10 pt-10 border-t border-zinc-200">
                <h2 className="text-2xl font-black text-zinc-900 mb-6 flex items-center gap-2">
                  <DollarSign className="w-6 h-6 text-green-600" />
                  Your Estimated Quote
                </h2>

                <div className="bg-zinc-50 p-6 border border-zinc-200 mb-6">
                  <div className="grid md:grid-cols-3 gap-6 mb-6">
                    <div className="text-center">
                      <div className="text-sm text-zinc-500 uppercase tracking-wide mb-1">Low Estimate</div>
                      <div className="text-2xl font-black text-zinc-700">{formatCurrency(estimate.estimate_low)}</div>
                    </div>
                    <div className="text-center bg-green-50 border border-green-200 p-4 -my-2">
                      <div className="text-sm text-green-700 uppercase tracking-wide mb-1">Typical Rate</div>
                      <div className="text-3xl font-black text-green-700">{formatCurrency(estimate.estimate_mid)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-zinc-500 uppercase tracking-wide mb-1">High Estimate</div>
                      <div className="text-2xl font-black text-zinc-700">{formatCurrency(estimate.estimate_high)}</div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center justify-center gap-2 text-zinc-600">
                      <MapPin className="w-4 h-4" />
                      {estimate.origin_location} → {estimate.destination_location}
                    </div>
                    <div className="flex items-center justify-center gap-2 text-zinc-600">
                      <Truck className="w-4 h-4" />
                      {estimate.equipment_type}
                    </div>
                    <div className="flex items-center justify-center gap-2 text-zinc-600">
                      ~{estimate.estimated_miles} miles
                    </div>
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="bg-amber-50 border border-amber-200 p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-amber-800 mb-1">Important Notice</h4>
                      <p className="text-sm text-amber-700">{estimate.disclaimer}</p>
                      <p className="text-sm text-amber-700 mt-2">
                        <strong>Fuel Surcharge:</strong> {estimate.fuel_surcharge_note}
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-zinc-900 p-6 text-center">
                  <h3 className="text-xl font-bold text-white mb-2">Want an Accurate Quote?</h3>
                  <p className="text-zinc-400 mb-4">Contact our team for precise pricing based on your specific requirements.</p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a 
                      href="tel:+18776809272"
                      className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-bold uppercase tracking-wide transition-colors"
                      data-testid="calc-call-btn"
                    >
                      <Phone className="w-4 h-4" />
                      1 (877) 680-9272
                    </a>
                    <Link 
                      to="/quote"
                      className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-zinc-900 px-6 py-3 font-bold uppercase tracking-wide transition-colors"
                      data-testid="calc-detailed-quote-btn"
                    >
                      Request Detailed Quote
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 border border-zinc-200">
              <Info className="w-8 h-8 text-red-600 mb-4" />
              <h3 className="font-bold text-zinc-900 mb-2">How We Calculate</h3>
              <p className="text-sm text-zinc-600">
                Our estimates are based on historical shipper rates, distance calculations, 
                and current market conditions. Equipment type significantly affects pricing.
              </p>
            </div>
            <div className="bg-white p-6 border border-zinc-200">
              <CheckCircle2 className="w-8 h-8 text-green-600 mb-4" />
              <h3 className="font-bold text-zinc-900 mb-2">What's Included</h3>
              <p className="text-sm text-zinc-600">
                Estimates include base freight charges and approximate fuel surcharges. 
                Accessorial charges (detention, lumper, etc.) are not included.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuoteCalculatorPage;
